import React, { useEffect, useState, } from 'react';
import '../admin.list.scss';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { ColumnsType, SorterResult, } from 'antd/lib/table/interface';
import { IPreviewSuperadminProfile, PermissionEnum, CrudEnum, TSuperadminProfileSortableListCol, } from 'atlas-shared';
import { Table, } from 'antd';
import { AdminListPageHeader, ListPageEmpty, } from '@Components';
import { IWithListProps, IWithListSorterProps, withList, } from '@Hocs/with.list';
import { useAbilities, useAuth, useSuperadminProfiles, useOrganizations, } from '@Hooks';
import { SuperadminProfileListColumns, } from '@Utils';
import { IAuth, } from '@Store';

const SuperadminProfileList = ({ filters, filtersCount, hasFilters, sorter, pagination, setQueryParams, resetQueryParams, }: IWithListProps<IPreviewSuperadminProfile, TSuperadminProfileSortableListCol>) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth: IAuth = useAuth();
  const superadmin_profiles = useSuperadminProfiles();
  const abilities = useAbilities();
  const [loading, setLoading, ] = useState<boolean>(true);
  const [rows, setRows, ] = useState<Array<any>>([]);
  const organizations = useOrganizations();
  
  const [showDeleted, setShowDeleted, ] = useState(false);
  const [deletedCount, setDeletedCount, ] = useState<number>(0);
  
  useEffect(() => {
    if (!superadmin_profiles.loaded)
      return;
      
    setLoading(false);
    setRows((!showDeleted ? superadmin_profiles.superadmin_profiles.filter(p => !p.deleted_at) : superadmin_profiles.superadmin_profiles || []).map(row => ({ ...row, key: row.id, })));
    setDeletedCount(superadmin_profiles.superadmin_profiles.filter(superadmin_profile => !!superadmin_profile.deleted_at).length);
  }, [superadmin_profiles, showDeleted, ]);

  const columns: ColumnsType<IPreviewSuperadminProfile> = React.useMemo(() => {
    return SuperadminProfileListColumns(t, navigate, showDeleted, organizations, abilities, auth, [], filters, sorter);
  }, [t, navigate, showDeleted, organizations, abilities, auth, filters, sorter, ]);

  return <div className='admin-list-view'>
    <AdminListPageHeader 
      t={t}
      title={t('SUPERADMIN_PROFILES')}
      filtersCount={filtersCount}
      reset={() => resetQueryParams()}
      deleted={[showDeleted, setShowDeleted, ]} deletedCount={deletedCount}
      addBtn={abilities.canImplicit(null, CrudEnum.Insert, PermissionEnum.SuperadminProfile) ? { callback: () => navigate('/admin/superadmin_profile/add'), } : undefined} 
    />
    <div className='table-wrp'>
      <Table
        key={loading ? 1 : 0}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
        }}
        onChange={(pagination, filters, _sorter) => {
          const sorter: Array<SorterResult<any>> = Array.isArray(_sorter) ? _sorter : _sorter?.columnKey ? [_sorter, ] : [];

          setQueryParams({
            pagination: { current: pagination.current as number, pageSize: pagination.pageSize as number, },
            filters: Object.fromEntries(Object.entries(filters).filter(([key, value, ]) => value !== null)),
            sorter: sorter
              .filter(s => !!s.order)
              .map(s => ({ column: s.columnKey as TSuperadminProfileSortableListCol, order: s.order as IWithListSorterProps<IPreviewSuperadminProfile, TSuperadminProfileSortableListCol>['order'], })),
          });
        }}
        locale={{
          emptyText: <ListPageEmpty t={t} hasFilters={hasFilters} hasRows={!!rows.length} reset={() => resetQueryParams()} />,
        }}
      />
    </div>
  </div>;
};

export const SuperadminProfileListView = React.memo(withList<IPreviewSuperadminProfile, TSuperadminProfileSortableListCol>(SuperadminProfileList, {
  filters: {},
  sorter: [{ column: 'created_at', order: 'descend', }, ],
  pagination: { current: 1, pageSize: 10, },
}));
